import React, { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

function fmtDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function defaultDateRange() {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  const startObj = new Date(now);
  startObj.setDate(startObj.getDate() - 7);
  const start = startObj.toISOString().slice(0, 10);
  return { start, end };
}

export default function Analytics() {
  const d = defaultDateRange();
  const [reportTypes, setReportTypes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [busyReport, setBusyReport] = useState("");

  const [startDate, setStartDate] = useState(d.start);
  const [endDate, setEndDate] = useState(d.end);
  const [minMovePct, setMinMovePct] = useState("1.5");

  const [msg, setMsg] = useState("");

  const loadTypes = async () => {
    setLoadingTypes(true);
    try {
      const res = await fetch(`${API}/api/analytics/report-types?t=${Date.now()}`, {
        cache: "no-store",
      });
      const data = await res.json();
      setReportTypes(data.report_types || []);
    } catch {
      setReportTypes([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const res = await fetch(`${API}/api/analytics/runs?limit=100`);
      const data = await res.json();
      setRuns(data.runs || []);
    } catch {
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  };

  useEffect(() => {
    loadTypes();
    loadRuns();
  }, []);

  const generateReport = async (reportType) => {
    setBusyReport(reportType);
    setMsg("");
    try {
      const res = await fetch(`${API}/api/analytics/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          report_type: reportType,
          start_date: startDate,
          end_date: endDate,
          min_move_pct: Number(minMovePct || 1.5),
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setMsg(`${reportType} generated (${data?.run?.row_count ?? 0} rows)`);
      await loadRuns();
    } catch (e) {
      setMsg(`Failed to generate ${reportType}`);
    } finally {
      setBusyReport("");
    }
  };

  const download = (runId, format = "csv") => {
    window.open(`${API}/api/analytics/download/${runId}?format=${format}`, "_blank");
  };

  const sortedRuns = useMemo(
    () => [...runs].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))),
    [runs]
  );

  const dashboard = useMemo(() => {
    const totalRuns = sortedRuns.length;
    const completed = sortedRuns.filter((r) => r.status === "COMPLETED").length;
    const failed = sortedRuns.filter((r) => r.status === "FAILED").length;
    const totalRows = sortedRuns.reduce((acc, r) => acc + Number(r.row_count || 0), 0);
    const latest = sortedRuns[0] || null;

    const byType = sortedRuns.reduce((acc, r) => {
      const key = r.report_type || "unknown";
      if (!acc[key]) acc[key] = { count: 0, rows: 0 };
      acc[key].count += 1;
      acc[key].rows += Number(r.row_count || 0);
      return acc;
    }, {});

    const topTypes = Object.entries(byType)
      .map(([type, v]) => ({ type, count: v.count, rows: v.rows }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    return {
      totalRuns,
      completed,
      failed,
      totalRows,
      latest,
      topTypes,
    };
  }, [sortedRuns]);

  const latestCompletedRunByType = useMemo(() => {
    const map = {};
    for (const run of sortedRuns) {
      if (run.status !== "COMPLETED") continue;
      const type = run.report_type || "unknown";
      if (!map[type]) {
        map[type] = run;
      }
    }
    return map;
  }, [sortedRuns]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Analytics</h2>
        <button
          onClick={() => {
            loadTypes();
            loadRuns();
          }}
          className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
          disabled={loadingRuns}
        >
          {loadingRuns ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <p className="text-gray-400 text-xs uppercase tracking-wider">Analytics Dashboard</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-[11px] text-gray-400">Total Runs</p>
            <p className="text-lg font-bold text-gray-800">{dashboard.totalRuns}</p>
          </div>
          <div className="bg-green-50 rounded-lg px-3 py-2">
            <p className="text-[11px] text-green-600">Completed</p>
            <p className="text-lg font-bold text-green-700">{dashboard.completed}</p>
          </div>
          <div className="bg-red-50 rounded-lg px-3 py-2">
            <p className="text-[11px] text-red-600">Failed</p>
            <p className="text-lg font-bold text-red-700">{dashboard.failed}</p>
          </div>
          <div className="bg-blue-50 rounded-lg px-3 py-2">
            <p className="text-[11px] text-blue-600">Rows Generated</p>
            <p className="text-lg font-bold text-blue-700">{dashboard.totalRows}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg px-3 py-2">
            <p className="text-[11px] text-yellow-600">Latest Run</p>
            <p className="text-xs font-semibold text-yellow-700 truncate">
              {dashboard.latest ? dashboard.latest.report_type : "—"}
            </p>
            <p className="text-[11px] text-yellow-600">
              {dashboard.latest ? fmtDateTime(dashboard.latest.created_at) : ""}
            </p>
          </div>
        </div>

        <div className="border border-gray-100 rounded-lg p-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-2">Most generated report types</p>
          {dashboard.topTypes.length === 0 ? (
            <p className="text-xs text-gray-500">No runs yet</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {dashboard.topTypes.map((t) => (
                <div key={t.type} className="bg-gray-50 rounded-lg px-2 py-2">
                  <p className="text-xs text-gray-700 font-medium truncate">{t.type}</p>
                  <p className="text-[11px] text-gray-500">{t.count} runs • {t.rows} rows</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <p className="text-gray-400 text-xs uppercase tracking-wider">Report Filters</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <label className="text-xs text-gray-600">
            Start Date
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
            />
          </label>
          <label className="text-xs text-gray-600">
            End Date
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
            />
          </label>
          <label className="text-xs text-gray-600">
            Missed Momentum %
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={minMovePct}
              onChange={(e) => setMinMovePct(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
            />
          </label>
          <div className="text-xs text-gray-500 flex items-end">
            Reports are generated only when you click Generate.
          </div>
        </div>
        {msg && <p className="text-xs text-blue-600">{msg}</p>}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Available Reports</p>
        {loadingTypes ? (
          <p className="text-xs text-gray-400">Loading report definitions…</p>
        ) : reportTypes.length === 0 ? (
          <p className="text-xs text-red-500">No report definitions available</p>
        ) : (
          <div className="space-y-2">
            {reportTypes.map((r) => (
              <div key={r.id} className="border border-gray-100 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-800 font-medium">{r.title}</p>
                  <p className="text-xs text-gray-500">{r.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => generateReport(r.id)}
                    disabled={busyReport === r.id}
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300"
                  >
                    {busyReport === r.id ? "Generating…" : "Generate"}
                  </button>
                  <button
                    onClick={() => download(latestCompletedRunByType[r.id]?.id, "csv")}
                    disabled={!latestCompletedRunByType[r.id]}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                  >
                    Latest CSV
                  </button>
                  <button
                    onClick={() => download(latestCompletedRunByType[r.id]?.id, "json")}
                    disabled={!latestCompletedRunByType[r.id]}
                    className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                  >
                    Latest JSON
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Generated Reports</p>
        {loadingRuns ? (
          <p className="text-xs text-gray-400">Loading runs…</p>
        ) : sortedRuns.length === 0 ? (
          <p className="text-xs text-gray-500">No generated reports yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-2">Created</th>
                  <th className="py-2 pr-2">Report</th>
                  <th className="py-2 pr-2">Range</th>
                  <th className="py-2 pr-2">Rows</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2">Download</th>
                </tr>
              </thead>
              <tbody>
                {sortedRuns.map((run) => (
                  <tr key={run.id} className="border-b border-gray-50">
                    <td className="py-2 pr-2 text-gray-600">{fmtDateTime(run.created_at)}</td>
                    <td className="py-2 pr-2 text-gray-800">{run.report_type}</td>
                    <td className="py-2 pr-2 text-gray-600">{run.start_date} → {run.end_date}</td>
                    <td className="py-2 pr-2 text-gray-700">{run.row_count ?? 0}</td>
                    <td className="py-2 pr-2">
                      <span className={`px-2 py-0.5 rounded-full ${run.status === "COMPLETED" ? "bg-green-50 text-green-700" : run.status === "FAILED" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"}`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="py-2 flex gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                        disabled={run.status !== "COMPLETED"}
                        onClick={() => download(run.id, "csv")}
                      >
                        CSV
                      </button>
                      <button
                        className="text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                        disabled={run.status !== "COMPLETED"}
                        onClick={() => download(run.id, "json")}
                      >
                        JSON
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
